









































































import { Component, Mixins, Prop } from "vue-property-decorator";
import { namespace } from "vuex-class";
import _ from "lodash";

/**
 * Utils
 */
import classes from "@/config/wow/classes";
import * as externalLinks from "@/config/external-links";
import * as imageLinks from "@/config/image-links";
import armorTypes from "@/config/wow/armor-types";
import difficulties from "@/config/wow/difficulties";

import { getElegantDateString, getLocaleStringFromDateString, getTimeSince } from "@/utils/date-helpers";

import { rank, classNameColorRank, classNameTierSetsComplete } from "@/utils/ranking-helpers";


/**
 * Components
 */
import CharacterCell from "@/components/_global/CharacterCell.vue";
import CharacterSpecCell from "@/components/_global/CharacterSpecCell.vue";
import CharacterSynchronizationStatus from "@/components/_global/CharacterSynchronizationStatus.vue";

/**
 * Mixins
 */
import mixinNow from "@/mixins/now-mixin";

/**
 * API
 */
import { GetOneTeamResponse } from "@/api/teams/get-one-team.response";
import { Guid } from "@/domain/guid";
import { GetRostersResponse } from "@/api/rosters/get-rosters.response";
import { sortByAsc } from "@/utils/sort-helpers";

/**
 * Types
 */

/**
 * VueX Modules
 */
const teamModule = namespace("team");

const setTypes = ["head", "shoulder", "chest", "hands", "legs"];

type Difficulty = "MYTHIC" | "HEROIC" | "NORMAL" | "LFR";

const classFromDifficulty: { [key: string]: string } = {
	"MYTHIC": "legendary",
	"HEROIC": "epic",
	"NORMAL": "rare"
};

interface ItemSetElement {
	ilvl: number;
	difficulty: Difficulty | null;
}

/**
 * Rosters great vault page.
 */
@Component({
	components: {
		CharacterCell,
		CharacterSpecCell,
		CharacterSynchronizationStatus
	}
})
export default class GearPage extends Mixins(mixinNow) {
	/// Injections
	classes = classes;
	externalLinks = externalLinks;
	imageLinks = imageLinks;
	armorTypes = armorTypes;
	difficulties = difficulties;
	getTimeSince = getTimeSince;
	getElegantDateString = getElegantDateString;
	getLocaleStringFromDateString = getLocaleStringFromDateString;

	classNameColorRank = classNameColorRank;
	classNameTierSetsComplete = classNameTierSetsComplete;

	setTypes = setTypes;
	classFromDifficulty = classFromDifficulty;

	/// Props
	@Prop()
	rosters!: GetRostersResponse.Roster[];

	/// Attributes
	isLoading = false;


	activeRosterID: Guid | null = null;

	/// VueX Modules
	@teamModule.State("team")
	currentTeam!: GetOneTeamResponse;

	get headers() {
		const result: any[] = [
			{
				text: "",
				value: "sync",
				align: "left",
				sortable: false,
				class: "collapsing"
			},
			{
				text: this.$t("character.name"),
				value: "name",
				align: "left",
				sortable: false
			}
		];

		if (this.$vuetify.breakpoint.lgAndUp) {
			result.push({
				text: this.$t("character.class"),
				value: "class",
				align: "left"
			});
		}

		result.push({
			text: this.$t("character.ilvl"),
			value: "averageItemLevelEquipped",
			align: "center",
			class: "collapsing"
		}, {
			text: this.$t("character.sockets"),
			value: "sockets",
			align: "center",
			class: "collapsing"
		});


		result.push(
			{
				text: this.$t("character.gear.head"),
				value: "set.head",
				align: "center",
				sortable: true,
				sort: sortByAsc(x => x?.ilvl ?? 0),
				class: "collapsing"
			},
			{
				text: this.$t("character.gear.shoulder"),
				value: "set.shoulder",
				align: "center",
				sortable: true,
				sort: sortByAsc(x => x?.ilvl ?? 0),
				class: "collapsing"
			},
			{
				text: this.$t("character.gear.chest"),
				value: "set.chest",
				align: "center",
				sortable: true,
				sort: sortByAsc(x => x?.ilvl ?? 0),
				class: "collapsing"
			},
			{
				text: this.$t("character.gear.hands"),
				value: "set.hands",
				align: "center",
				sortable: true,
				sort: sortByAsc(x => x?.ilvl ?? 0),
				class: "collapsing"
			},
			{
				text: this.$t("character.gear.legs"),
				value: "set.legs",
				align: "center",
				sortable: true,
				sort: sortByAsc(x => x?.ilvl ?? 0),
				class: "collapsing"
			},
			{
				text: "Total",
				value: "set.total",
				align: "center",
				sortable: true,
				class: "collapsing"
			}
		);

		return result;
	}

	get displayedData() {
		if (this.displayedRoster == null) {
			return [];
		}

		return this.displayedRoster.characters
			.map((x) => ({
				...x,
				set: x.gearItems ? setTypes.reduce((prev, curr) => {
					const gearItem = x.gearItems.find((gearItem) => gearItem.type == curr && gearItem.isSet);
					return {
						...prev,
						[curr]: gearItem != null ? {
							ilvl: gearItem.itemLevel,
							difficulty: gearItem.difficulty
						} : null
					};
				}, {
					total: x.gearItems.filter((gearItem) => gearItem.isSet).length
				}) : null,
				sockets: x.gearItems ? _.sumBy(x.gearItems, (gearItem) => gearItem.sockets) : null
			}))
			.sort((a, b) => {
				return ((a.role < b.role) ? 1 : (a.role > b.role) ? -1 : 0)
					|| (b.averageItemLevelEquipped - a.averageItemLevelEquipped)
					|| ((a.name < b.name) ? -1 : (a.name > b.name) ? 1 : 0);
			});
	}

	/**
	 * The current roster to display.
	 */
	get displayedRoster(): GetRostersResponse.Roster | null {
		return this.rosters.find(x => x.id == this.$route.params.rosterId) ?? null;
	}

	/// Methods
	async created() {
		this.isLoading = true;

		if (this.rosters && this.rosters.length > 0) {
			this.activeRosterID = this.rosters[0].id;
		}

		this.isLoading = false;
	}

	getClassFromDifficulty(itemSetElement: { [key: string]: ItemSetElement }, setType: string) {

		return [
			"warcraft",
			"colored",
			itemSetElement != null && itemSetElement[setType] && classFromDifficulty.hasOwnProperty(itemSetElement[setType].difficulty as string)
				? classFromDifficulty[itemSetElement[setType].difficulty as string]
				: undefined
		];
		//return item.set != null && item.set[setType] && classFromDifficulty ? classFromDifficulty[] : undefined;
	}


	getSlotName(gearType: string) {
		return `item.set.${gearType}`;
	}

	get rankFunctionGreatVaultScore() {
		return rank(this.displayedData.map((c) => c.greatVaultScore));
	}

	get rankFunctionIlvl() {
		return rank(this.displayedData.map((c) => c.averageItemLevelEquipped));
	}

	get rankFunctionSockets() {
		return rank(this.displayedData.map((c) => c.sockets ?? 0));
	}
}
